import { create } from 'zustand';
import { persist, subscribeWithSelector, StateStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { shallow } from 'zustand/shallow';
import { getCookie, setCookie, deleteCookie } from 'cookies-next';
import { useEffect } from 'react';

// Initial user data structure
const initialUserData = {
  profile: null,
  accounts: [],
  cards: [],
  transactions: [],
  analytics: null,
  settings: null,
  security: null,
  notifications: null,
  privacy: null,
  financialData: null,
  scheduledTransfers: [],
  supportTickets: []
};

// Debug utility to log the entire store state
const logState = (context, state) => {
  if (typeof window === 'undefined') return;
  
  console.log(`🔍 ===== ZUSTAND STORE DEBUG [${context}] =====`);
  console.log('📈 Complete Store State:');
  console.log('  - user:', state.user);
  console.log('  - user exists:', !!state.user);
  console.log('  - user._id:', state.user?._id);
  console.log('  - user.email:', state.user?.email);
  console.log('  - user.firstName:', state.user?.firstName);
  console.log('  - user.lastName:', state.user?.lastName);
  console.log('  - user.address:', state.user?.address);
  console.log('  - user.address details:', state.user?.address ? {
      street: state.user?.address?.street,
      city: state.user?.address?.city,
      state: state.user?.address?.state,
      zipCode: state.user?.address?.zipCode,
      country: state.user?.address?.country
    } : 'NOT_FOUND');
  console.log('  - user.compliance:', state.user?.compliance);
  console.log('  - loading:', state.loading);
  console.log('  - error:', state.error);
  console.log('  - isAuthenticated:', state.isAuthenticated);
  console.log('  - authChecked:', state.authChecked);
  console.log('  - userData keys:', Object.keys(state.userData));
  console.log('  - userData.profile:', state.userData.profile);
  console.log('  - userData.profile firstName:', state.userData?.profile?.firstName);
  console.log('  - userData.profile lastName:', state.userData?.profile?.lastName);
  console.log('  - userData.profile address:', state.userData?.profile?.address);
  console.log('  - userData.accounts length:', state.userData.accounts.length);
  console.log('  - userData.cards length:', state.userData.cards.length);
  console.log('  - userData.transactions length:', state.userData.transactions.length);
  
  const token = getCookie('token');
  console.log('  - token exists in cookies:', !!token);
  if (token) {
    console.log('  - token preview:', token.substring(0, 20) + '...');
  }
  console.log('🔍 ===== END ZUSTAND STORE DEBUG =====');
};

// Track the rehydration state
let rehydrated = false;
let rehydratedWithAuth = false;

// Custom storage implementation to handle SSR safely
const safeStorage = {
  getItem: (name) => {
    try {
      return localStorage.getItem(name);
    } catch (error) {
      // On server-side, localStorage is not available
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value);
    } catch (error) {
      // On server-side, localStorage is not available
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name);
    } catch (error) {
      // On server-side, localStorage is not available
    }
  },
};

export const useUserStore = create(subscribeWithSelector(persist(
  immer((set, get) => ({
    // --- STATE ---
    user: null,
    userData: { ...initialUserData },
    isAuthenticated: false,
    authChecked: false,
    loading: false,
    error: null,

    // --- ACTIONS ---
    // Using immer, we can "mutate" state directly in our actions
       checkAuthentication: async () => {
      console.log('🔐 Checking auth status via API');
      try {
        set(state => { state.loading = true; });

        // Always ask the server. The real token is httpOnly, so the browser
        // can't read it, and a cookie existing doesn't mean the token is valid.
        const response = await fetch('/api/auth/verify', {
          method: 'GET',
          credentials: 'include'
        });

        const isAuthenticated = response.status === 200;
        console.log('🔐 Auth API response:', { status: response.status, isAuthenticated });

        set(state => {
          state.isAuthenticated = isAuthenticated;
          state.authChecked = true;
          state.loading = false;

          if (!isAuthenticated) {
            state.user = null;
            state.userData = { ...initialUserData };
          }
        });

        return isAuthenticated;
      } catch (error) {
        console.error('❌ Auth check error:', error);
        set(state => {
          state.loading = false;
          state.error = error.message;
          state.authChecked = true;
          state.isAuthenticated = false;
        });
        return false;
      }
    },

    fetchAllUserData: async (retryCount = 0) => {
      set(state => { state.loading = true; state.error = null; });
      console.log('📡 Fetching user data from API...');
      try {
        const response = await fetch('/api/dashboard/data', { method: 'GET', credentials: 'include' });
        if (!response.ok) {
          if (response.status === 500 && retryCount < 2) {
            console.log(`⚠️ Server error (${response.status}), retrying... (${retryCount + 1}/2)`);
            await new Promise(resolve => setTimeout(resolve, 1000));
            return get().fetchAllUserData(retryCount + 1);
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const apiData = await response.json();
        console.log('✅ API Response received:', apiData);
        console.log('🔐 Security data from API:', {
          exists: !!apiData.security,
          hasLbtCode: apiData.security?.hasLbtCode,
          lbtCode: apiData.security?.lbtCode ? 'EXISTS' : 'MISSING',
          hasIbtCode: apiData.security?.hasIbtCode,
          ibtCode: apiData.security?.ibtCode ? 'EXISTS' : 'MISSING',
          checkoutPin: apiData.security?.checkoutPin ? 'EXISTS' : 'MISSING'
        });
      
      // Log critical profile fields to ensure they exist in the response
      if (apiData.user) {
        console.log('👤 User profile fields check:', { 
          firstName: apiData.user?.firstName || 'MISSING', 
          lastName: apiData.user?.lastName || 'MISSING',
          email: apiData.user?.email || 'MISSING',
          hasAddress: apiData.user?.address ? 'YES' : 'MISSING'
        });
      } else {
        console.warn('⚠️ No user data in API response!');
      }
      
      // Make sure we have all the user profile data properly extracted
      const userProfile = apiData.user || null;
      
      // Log the exact structure we're working with
      console.log('📋 User profile data being stored:', {
        _id: userProfile?._id,
        firstName: userProfile?.firstName,
        lastName: userProfile?.lastName,
        email: userProfile?.email,
        address: userProfile?.address,
      });
      
      // Store the data in the Zustand store - critical fields should be available at both levels
      set(state => {
        // Ensure user object has all profile fields
        state.user = {
          ...userProfile,
          // Make sure these critical fields are always set, even if they're null/undefined
          firstName: userProfile?.firstName,
          lastName: userProfile?.lastName,
          email: userProfile?.email,
          address: userProfile?.address,
        };
        
        // Also ensure userData.profile has the same fields
        state.userData = {
          // Make sure profile has all fields including address
          profile: {
            ...userProfile,
            firstName: userProfile?.firstName,
            lastName: userProfile?.lastName,
            email: userProfile?.email,
            address: userProfile?.address,
          },
          accounts: apiData.accounts || [],
          cards: apiData.cards || [],
          transactions: apiData.transactions || [],
          analytics: apiData.analytics || null,
          settings: apiData.settings || null,
          security: apiData.security || null, // This now contains security codes data
          notifications: apiData.notifications || null,
          privacy: apiData.privacy || null,
          financialData: apiData.financialData || null,
          scheduledTransfers: apiData.scheduledTransfers || [],
          supportTickets: apiData.supportTickets || []
        };
        
        // Log what was actually stored
        console.log('🔐 Security data STORED in state:', {
          exists: !!state.userData.security,
          hasLbtCode: state.userData.security?.hasLbtCode,
          lbtCode: state.userData.security?.lbtCode ? 'STORED' : 'NOT STORED',
          hasIbtCode: state.userData.security?.hasIbtCode,
          ibtCode: state.userData.security?.ibtCode ? 'STORED' : 'NOT STORED'
        });
        
        // Enhanced security data logging with better formatting and checks
        if (apiData.security) {
          console.log('🔐 Security data details:', {
            // Security metadata
            exists: true,
            
            // Checkout PIN
            hasCheckoutPin: apiData.security.hasCheckoutPin || false,
            checkoutPinLength: apiData.security.checkoutPin?.length,
            
            // LBT Code
            hasLbtCode: apiData.security.hasLbtCode || false,
            lbtCodeLength: apiData.security.lbtCode?.length,
            
            // IBT Code
            hasIbtCode: apiData.security.hasIbtCode || false,
            ibtCodeLength: apiData.security.ibtCode?.length,
            
            // IMF Code
            hasImfCode: apiData.security.hasImfCode || false,
            imfCodeLength: apiData.security.imfCode?.length
          });
        } else {
          console.warn('🔐 Warning: No security data available in API response')
        }
      });
      
      // Verify data was set correctly in Zustand store
      const updatedState = get();
      console.log('✅ Store updated. Profile data verification:', {
        storeHasUser: !!updatedState.user,
        firstName: updatedState.user?.firstName || updatedState.userData?.profile?.firstName || 'MISSING',
        userData_profile_exists: !!updatedState.userData?.profile
      });
      
      // Log security data for debugging
      console.log('🔐 Security data loaded:', {
        // Security metadata
        securityDataExists: !!updatedState.userData?.security,
        
        // Checkout PIN data
        hasCheckoutPin: updatedState.userData?.security?.hasCheckoutPin || false,
        isCheckoutPinRevealed: updatedState.userData?.security?.isCheckoutPinRevealed || false,
        hasActualPin: !!updatedState.userData?.security?.checkoutPin,
        pinLength: updatedState.userData?.security?.checkoutPin?.length,
        pinFirstChar: updatedState.userData?.security?.checkoutPin ? updatedState.userData?.security?.checkoutPin.charAt(0) + '****' : 'none',
        
        // LBT code data
        hasLbtCode: updatedState.userData?.security?.hasLbtCode || false,
        lbtCodeExists: !!updatedState.userData?.security?.lbtCode,
        lbtCodeLength: updatedState.userData?.security?.lbtCode?.length,
        lbtCodeFirstChar: updatedState.userData?.security?.lbtCode ? updatedState.userData?.security?.lbtCode.charAt(0) + '****' : 'none',
        
        // IBT code data
        hasIbtCode: updatedState.userData?.security?.hasIbtCode || false,
        ibtCodeExists: !!updatedState.userData?.security?.ibtCode,
        ibtCodeLength: updatedState.userData?.security?.ibtCode?.length,
        ibtCodeFirstChar: updatedState.userData?.security?.ibtCode ? updatedState.userData?.security?.ibtCode.charAt(0) + '****' : 'none',
        
        // IMF code data
        hasImfCode: updatedState.userData?.security?.hasImfCode || false,
        imfCodeExists: !!updatedState.userData?.security?.imfCode,
        imfCodeLength: updatedState.userData?.security?.imfCode?.length,
        imfCodeFirstChar: updatedState.userData?.security?.imfCode ? updatedState.userData?.security?.imfCode.charAt(0) + '****' : 'none'
      });
      
      // Special debug for cards with PIN data
      if (updatedState.userData?.cards && updatedState.userData.cards.length > 0) {
        console.log('🔍 CARD PIN DEBUG - Cards loaded in store:', updatedState.userData.cards.map(card => ({
          id: card.id,
          name: card.name,
          pin: card.pin,
          pinType: typeof card.pin,
          pinPresent: !!card.pin,
          pinIsFalse: card.pin === false,
          pinIsString: typeof card.pin === 'string'
        })));
      } else {
        console.log('🔍 CARD PIN DEBUG - No cards found in store');
      }
      
      set(state => { 
        state.loading = false;
        state.isAuthenticated = true;
        state.authChecked = true;
      });
      
      console.log('✅ User data updated successfully');
      } catch (err) {
        console.error('❌ Error fetching user data:', err);
        set(state => {
          state.loading = false;
          state.error = err.message;
          if (err.message.includes('401') || err.message.includes('403')) {
            state.user = null;
            state.userData = { ...initialUserData };
            state.isAuthenticated = false;
          }
        });
      }
    },

    handleLoginSuccess: async ({ user, token }) => {
      console.log('🎉 Login success - updating store state...');
      if (!user || !token) {
        console.error('❌ handleLoginSuccess: Missing user or token');
        return;
      }
      
      
      // Log user data from login response
      console.log('📜 Login user data:', {
        _id: user?._id,
        firstName: user?.firstName,
        lastName: user?.lastName,
        email: user?.email,
        address: user?.address
      });
      
      // Use same structure as fetchAllUserData for consistency
      set(state => {
        // Ensure user object has all profile fields
        state.user = {
          ...user,
          firstName: user?.firstName,
          lastName: user?.lastName,
          email: user?.email,
          address: user?.address,
        };
        
        // Also ensure userData.profile has the same fields
        state.userData = {
          ...state.userData,
          profile: {
            ...user,
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
            address: user?.address,
          }
        };
        
        state.isAuthenticated = true;
        state.authChecked = true;
        state.loading = false;
        state.error = null;
      });
      
      // Still fetch all user data to get complete dashboard data
      await get().fetchAllUserData();
      get().logStoreState('LOGIN_SUCCESS');
    },

    logout: async () => {
      console.log('🚪 Logging out user...');
      try {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
        deleteCookie('token');
        set(state => {
          state.user = null;
          state.userData = { ...initialUserData };
          state.isAuthenticated = false;
          state.authChecked = true;
          state.loading = false;
          state.error = null;
        });
        console.log('✅ Logout successful');
      } catch (error) {
        console.error('❌ Logout error:', error);
      }
    },

    logStoreState: (context) => {
      const currentState = get();
      logState(context, currentState);
    },

    // Account operations
    updateAccount: (accountId, updateData) => {
      console.log('💰 ACCOUNT DEBUG - Updating account:', accountId);
      console.log('💰 ACCOUNT DEBUG - Update data:', updateData);
      
      set(state => {
        const accountIndex = state.userData.accounts.findIndex(account => account.id === accountId);
        if (accountIndex !== -1) {
          // Account before update
          console.log('💰 ACCOUNT DEBUG - Account before update:', { 
            id: state.userData.accounts[accountIndex].id,
            balance: state.userData.accounts[accountIndex].balance 
          });
          
          // Apply updates to the account
          Object.assign(state.userData.accounts[accountIndex], updateData);
          
          // Account after update
          console.log('💰 ACCOUNT DEBUG - Account after update:', { 
            id: state.userData.accounts[accountIndex].id,
            balance: state.userData.accounts[accountIndex].balance 
          });
        } else {
          console.error('❌ Account not found with ID:', accountId);
        }
      });
      
      return get().userData.accounts;
    },

    // Card operations
    updateCard: (cardId, updateData) => {
      // PIN Debugging - Clear specific logging for PIN updates
      if (updateData.hasOwnProperty('pin')) {
        console.log('🔍 PIN DEBUG - Updating card PIN:', cardId);
        console.log('🔍 PIN DEBUG - PIN value type:', typeof updateData.pin);
        console.log('🔍 PIN DEBUG - PIN value:', updateData.pin);
      }
      
      set(state => {
        const cardIndex = state.userData.cards.findIndex(card => card.id === cardId);
        if (cardIndex !== -1) {
          // Card before update
          if (updateData.hasOwnProperty('pin')) {
            console.log('🔍 PIN DEBUG - Card before update:', { 
              id: state.userData.cards[cardIndex].id,
              pin: state.userData.cards[cardIndex].pin 
            });
          }
          
          // Apply updates to the card
          Object.assign(state.userData.cards[cardIndex], updateData);
          
          // Card after update
          if (updateData.hasOwnProperty('pin')) {
            console.log('🔍 PIN DEBUG - Card after update:', { 
              id: state.userData.cards[cardIndex].id,
              pin: state.userData.cards[cardIndex].pin 
            });
          }
        } else {
          console.error('❌ Card not found with ID:', cardId);
        }
      });
      
      // Return the updated cards array
      if (updateData.hasOwnProperty('pin')) {
        const updatedCards = get().userData.cards;
        const updatedCard = updatedCards.find(card => card.id === cardId);
        console.log('🔍 PIN DEBUG - Updated card in store:', updatedCard ? {
          id: updatedCard.id,
          pin: updatedCard.pin,
          pinType: typeof updatedCard.pin
        } : 'Not found');
      }
      
      return get().userData.cards;
    },

    // Profile update operations
    updateProfile: async (profileData) => {
      console.log('🔄 Updating profile:', profileData);
      set(state => { state.loading = true; });
      
      try {
        const response = await fetch('/api/user/profile', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(profileData),
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('✅ Profile updated successfully:', result);
        
        // Refresh the user data
        await get().fetchAllUserData();
        
        set(state => { 
          state.loading = false;
          state.error = null;
        });
        
        return { success: true };
      } catch (error) {
        console.error('❌ Profile update error:', error);
        set(state => {
          state.loading = false;
          state.error = error.message;
        });
        return { success: false, message: error.message };
      }
    },

    // Security settings update operations
    updateSecurity: async (securityData) => {
      console.log('🔄 Updating security settings:', securityData);
      set(state => { state.loading = true; });
      
      try {
        const response = await fetch('/api/user/security', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(securityData),
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('✅ Security settings updated successfully:', result);
        
        // Refresh the user data
        await get().fetchAllUserData();
        
        set(state => { 
          state.loading = false;
          state.error = null;
        });
        
        return { success: true };
      } catch (error) {
        console.error('❌ Security settings update error:', error);
        set(state => {
          state.loading = false;
          state.error = error.message;
        });
        return { success: false, message: error.message };
      }
    },

    // Notifications update operations
    updateNotifications: async (notificationsData) => {
      console.log('🔄 Updating notification preferences:', notificationsData);
      set(state => { state.loading = true; });
      
      try {
        const response = await fetch('/api/user/notifications', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(notificationsData),
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('✅ Notification preferences updated successfully:', result);
        
        // Refresh the user data
        await get().fetchAllUserData();
        
        set(state => { 
          state.loading = false;
          state.error = null;
        });
        
        return { success: true };
      } catch (error) {
        console.error('❌ Notification preferences update error:', error);
        set(state => {
          state.loading = false;
          state.error = error.message;
        });
        return { success: false, message: error.message };
      }
    },

    // Privacy settings update operations
    updatePrivacy: async (privacyData) => {
      console.log('🔄 Updating privacy settings:', privacyData);
      set(state => { state.loading = true; });
      
      try {
        const response = await fetch('/api/user/privacy', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(privacyData),
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('✅ Privacy settings updated successfully:', result);
        
        // Refresh the user data
        await get().fetchAllUserData();
        
        set(state => { 
          state.loading = false;
          state.error = null;
        });
        
        return { success: true };
      } catch (error) {
        console.error('❌ Privacy settings update error:', error);
        set(state => {
          state.loading = false;
          state.error = error.message;
        });
        return { success: false, message: error.message };
      }
    },
  })),

  {
    name: 'user-store',
    storage: safeStorage, // Use the SSR-safe storage
    partialize: (state) => ({
      user: state.user,
      userData: state.userData,
      isAuthenticated: state.isAuthenticated,
      authChecked: state.authChecked,
    }),
       onRehydrateStorage: () => (restoredState, error) => {
      console.log('💾 Rehydration error:', error || 'None');
      rehydrated = true;

      setTimeout(async () => {
        if (typeof window === 'undefined') return;

        try {
          // Always verify with the server on page load
          const response = await fetch(`${window.location.origin}/api/auth/verify`, {
            method: 'GET',
            credentials: 'include'
          });
          const isAuth = response.status === 200;
          console.log('🔐 Auth API response:', { status: response.status, isAuth });

          if (isAuth) {
            rehydratedWithAuth = true;
            useUserStore.setState({
              isAuthenticated: true,
              authChecked: true
            });

            if (!restoredState?.user || Object.keys(restoredState.user).length === 0) {
              useUserStore.getState().fetchAllUserData();
            }
          } else {
            useUserStore.setState({
              isAuthenticated: false,
              authChecked: true,
              user: null,
              userData: { ...initialUserData }
            });
          }
        } catch (error) {
          console.error('❌ Auth verification error:', error);
          useUserStore.setState({
            isAuthenticated: false,
            authChecked: true,
            error: 'Authentication verification failed'
          });
        }
      }, 0);
    },
  }
)));

// ====================================================================
// HOOKS & SELECTORS
// ====================================================================

// Note: We are exporting individual hooks for each piece of state and action
// to prevent infinite re-render loops. Subscribing to the entire store
// or returning new objects from selectors is an anti-pattern.

// Expose the store globally for debugging
if (typeof window !== 'undefined') {
  window.userStore = useUserStore;
  console.log('📡 STORE DEBUG: Exposed userStore globally');
  
  // Immediate debug log of current state
  const currentState = useUserStore.getState();
  console.log('📡 STORE DEBUG: Current state:', {
    user: currentState.user,
    userExists: !!currentState.user,
    firstName: currentState.user?.firstName,
    lastName: currentState.user?.lastName,
    address: currentState.user?.address,
    isAuthenticated: currentState.isAuthenticated
  });
}

// State Hooks with added logging
export const useUser = () => {
  const user = useUserStore((state) => state.user);
  if (typeof window !== 'undefined') {
    console.log('📡 useUser hook called, returned:', {
      userExists: !!user,
      firstName: user?.firstName,
      lastName: user?.lastName,
      hasAddress: !!user?.address
    });
  }
  return user;
};
export const useUserData = () => useUserStore((state) => state.userData);
export const useIsAuthenticated = () => useUserStore((state) => state.isAuthenticated);
export const useAuthChecked = () => useUserStore((state) => state.authChecked);
export const useIsLoading = () => useUserStore((state) => state.loading);
export const useError = () => useUserStore((state) => state.error);

// Action Hooks
export const useSetUser = () => useUserStore((state) => state.setUser);
export const useSetUserData = () => useUserStore((state) => state.setUserData);
export const useSetLoading = () => useUserStore((state) => state.setLoading);
export const useSetError = () => useUserStore((state) => state.setError);
export const useSetAuthenticated = () => useUserStore((state) => state.setAuthenticated);
export const useCheckAuthentication = () => useUserStore((state) => state.checkAuthentication);
export const useFetchAllUserData = () => useUserStore((state) => state.fetchAllUserData);
export const useHandleLoginSuccessAction = () => useUserStore((state) => state.handleLoginSuccess);
export const useLogout = () => useUserStore((state) => state.logout);
export const useUpdateProfile = () => useUserStore((state) => state.updateProfile);
export const useUpdateSecurity = () => useUserStore((state) => state.updateSecurity);
export const useUpdateNotifications = () => useUserStore((state) => state.updateNotifications);
export const useUpdatePrivacy = () => useUserStore((state) => state.updatePrivacy);
export const useRefreshUserData = () => useUserStore((state) => state.refreshUserData);
export const useUpdateCard = () => useUserStore((state) => state.updateCard);
export const useUpdateAccount = () => useUserStore((state) => state.updateAccount);
export const useLogStoreState = () => useUserStore((state) => state.logStoreState);

// Split the useInitializeUserStore to prevent object recreation that causes infinite loops
export const useAuthState = () => useUserStore((state) => state.isAuthenticated);
export const useAuthCheckedState = () => useUserStore((state) => state.authChecked);
export const useUserState = () => useUserStore((state) => state.user);
export const useFetchUserDataAction = () => useUserStore((state) => state.fetchAllUserData);

export const useInitializeUserStore = () => {
  // Use individual selectors instead of object destructuring to prevent recreation
  const isAuthenticated = useAuthState();
  const authChecked = useAuthCheckedState();
  const user = useUserState();
  const loading = useUserStore(state => state.loading);
  const fetchAllUserData = useFetchUserDataAction();
  const checkAuthentication = useUserStore(state => state.checkAuthentication);

  useEffect(() => {
    console.log('🔄 Auth state check in useInitializeUserStore:', { 
      isAuthenticated, 
      authChecked, 
      rehydrated, 
      rehydratedWithAuth,
      userExists: !!user,
      loading
    });
    
    // Check authentication status first on mount/initialization
    if (!authChecked) {
      console.log('🔐 Checking authentication status on mount...');
      checkAuthentication().then(isAuth => {
        // After authentication is checked and if we're authenticated, fetch user data
        if (isAuth && !user) {
          console.log('🚀 Fetching user data after auth check...');
          fetchAllUserData();
        }
      });
    }
    // If we're already auth checked and authenticated but have no user data 
    // or rehydrated with auth flag, fetch the data
    else if (authChecked && isAuthenticated && (!user || rehydratedWithAuth)) {
      console.log('🚀 Fetching user data on initial load/rehydration...');
      fetchAllUserData();
      // Reset the flag after fetching
      rehydratedWithAuth = false;
    }
  }, [isAuthenticated, authChecked, user, fetchAllUserData, checkAuthentication]);

  // Return individual primitives, not a newly created object
  return { isAuthenticated, authChecked };
};
